import React from 'react'
import {Button} from '../../../../components/library/buttons'
import {FormModal} from '../../../../components/modals'
import {ConfirmIntegrationActionModalIntegrationFragment} from './generated/confirm'
import styled from 'styled-components'
import {WarningBanner} from '../../../../components/warning'
import {upperCaseFirst} from '../../../../lib/helpers'
import {assertUnreachable, isDefined} from '../../../../common/types/helpers'
import {gql} from '@apollo/client'
import {IntegrationType} from '../../../../data/workbench-api/generated/types'

const ConfirmModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  p {
    padding: 0;
    margin: 0;
  }
  ul {
    padding: 10px 20px;
    margin: 0;
    li {
      margin-top: 4px;
    }
  }
  .lighter {
    color: ${({theme}) => theme.grey65};
  }
`
const StatusListsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

type LinkedStatus = 'unassociated' | 'draft' | 'published' | 'mixed'
export type ConfirmSegmentActionModalMode = 'delete' | 'disable'

type ConfirmSegmentActionModalProps = {
  mode: ConfirmSegmentActionModalMode
  integration: ConfirmIntegrationActionModalIntegrationFragment
  onClose: () => void
  onConfirm: () => void
  description?: string
}

const getLinkedStatus = (integration: {
  associatedActionFlows?: {usedInPublishedVersion: boolean}[]
}): LinkedStatus =>
  !integration.associatedActionFlows?.length
    ? 'unassociated'
    : integration.associatedActionFlows?.every(af => af.usedInPublishedVersion)
    ? 'published'
    : integration.associatedActionFlows?.every(af => !af.usedInPublishedVersion)
    ? 'draft'
    : 'mixed'

const labelForType = (integrationType: IntegrationType) => {
  switch (integrationType) {
    case IntegrationType.SfmcJb:
      return 'Salesforce'
    case IntegrationType.MarketoSsfs:
      return 'Marketo'
    default: {
      assertUnreachable(integrationType)
    }
  }
}

/** a generic description, custom messages can be passed into Modal  */
const getDescription = (
  mode: 'delete' | 'disable',
  linkedStatus: LinkedStatus,
  integrationType: IntegrationType
) => {
  const typeLabel = labelForType(integrationType)
  if (!typeLabel) {
    return undefined
  }

  if (mode === 'delete') {
    if (linkedStatus === 'unassociated')
      return 'Deletion is permanent. Are you sure you want to delete this integration?'
    if (linkedStatus === 'draft')
      return `Before you can delete this integration you'll need to remove the connector from the following card templates.`
    // mixed & published
    return `Before you can delete this integration you'll need to remove the connector from the following card templates. For published card templates you'll then need to publish a new version.`
  }

  if (mode === 'disable') {
    if (linkedStatus === 'unassociated')
      return `This ${typeLabel} integration is not connected to any draft or published card templates. You can easily re-enable this integration at any time through the overflow menu.`
    if (linkedStatus === 'draft')
      return `If you disable this integration, the following card templates can not be triggered via this ${typeLabel} integration.`
    // mixed & published
    return `If you disable this integration, the following card templates can no longer be triggered via this ${typeLabel} integration. Published cards will remain published, but not triggered via this integration.`
  }
  assertUnreachable(mode)
}

// Number of flows to show under each status
const LINKED_AF_LIMIT = 5

export const ConfirmIntegrationActionModal = ({
  mode,
  integration,
  onClose,
  onConfirm,
  description
}: ConfirmSegmentActionModalProps) => {
  const linkedStatus = getLinkedStatus(integration)

  const title = mode === 'delete' ? 'Delete integration' : 'Disable integration'
  const confirmationDescription =
    description ??
    getDescription(mode, linkedStatus, integration.integrationType)

  // Don't show a delete button if there are associated flows
  const submitText =
    mode === 'disable'
      ? 'Disable integration'
      : linkedStatus === 'unassociated'
      ? 'Delete integration'
      : undefined
  return (
    <FormModal
      title={title}
      handleClose={onClose}
      onSubmit={() => {
        onConfirm()
        onClose()
      }}
      renderButtons={(valid, close) => (
        <>
          {submitText && (
            <Button
              data-testid={'button-save'}
              type="submit"
              appearance={
                mode === 'delete' || linkedStatus !== 'unassociated'
                  ? 'warning'
                  : 'primary'
              }
              disabled={!valid}
              size="large"
            >
              {mode === 'delete' ? 'Delete integration' : 'Disable integration'}
            </Button>
          )}
          <Button
            data-testid={'button-cancel'}
            onClick={close}
            appearance="tint"
            size="large"
          >
            {submitText ? 'Cancel' : 'Close'}
          </Button>
        </>
      )}
    >
      <ConfirmModalLayout>
        {linkedStatus === 'unassociated' ? (
          <p>{confirmationDescription}</p>
        ) : (
          <>
            <WarningBanner
              mode={mode === 'delete' ? 'error' : 'warning'}
              message={`This integration is linked to ${
                linkedStatus === 'mixed' ? 'published and draft' : linkedStatus
              } card templates.`}
            />
            <p>{confirmationDescription}</p>
            <StatusListsWrapper>
              {(['published', 'draft'] as const).map(status => {
                const actionFlows = integration.associatedActionFlows
                  ?.filter(isDefined)
                  .filter(
                    af =>
                      (status === 'published' && af.usedInPublishedVersion) ||
                      (status === 'draft' && !af.usedInPublishedVersion)
                  )
                if (!actionFlows?.length) return null

                // only show the first LINKED_AF_SOFT_LIMIT action flows
                const truncate = actionFlows.length > LINKED_AF_LIMIT
                const AFsToShow = truncate
                  ? LINKED_AF_LIMIT - 1 // only show 'N more...' if N is more than 2
                  : undefined

                return (
                  <div key={status}>
                    <strong>{upperCaseFirst(status)} card templates</strong>
                    <ul>
                      {actionFlows.slice(0, AFsToShow).map(af => (
                        <li key={af.id}>
                          {af.name || 'Untitled card'}{' '}
                          <span className="lighter">ID: {af.id}</span>
                        </li>
                      ))}
                      {truncate && (
                        <li>{`${
                          actionFlows.length - (AFsToShow ?? 0)
                        } more...`}</li>
                      )}
                    </ul>
                  </div>
                )
              })}
            </StatusListsWrapper>
          </>
        )}
      </ConfirmModalLayout>
    </FormModal>
  )
}

ConfirmIntegrationActionModal.fragments = {
  integration: gql`
    fragment ConfirmIntegrationActionModalIntegration on IntegrationConfiguration {
      id
      name
      integrationType
      associatedActionFlows {
        id
        name
        usedInPublishedVersion
      }
    }
  `
}
